import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import LanguageSelector from "@/components/LanguageSelector";
import { format } from "date-fns";
import {
  BarChart3, Users, FileSpreadsheet, Upload, LogOut, Trash2, User, Database, UserPlus, Lock, Eye, EyeOff, X,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Filter, Key, Mail, CalendarIcon, Clock, Home, Settings, ArrowUp, ArrowDown, ArrowUpDown, Edit, Globe, Save, UserX, AlertTriangle, Download, Tag, Languages, Hash
} from "lucide-react";
import "@/styles/fiori.css";


const PasswordCell = ({ userId, token }) => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    if (visible) { setVisible(false); return; }
    if (password) { setVisible(true); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${userId}/password`, { headers: { Authorization: `Bearer ${token}` } });
      setPassword(res.data.password);
      setVisible(true);
    } catch { setPassword(null); }
    setLoading(false);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontFamily: 'monospace', fontSize: '0.8125rem' }}>
      <span style={{ color: 'var(--sapContent_LabelColor)', minWidth: '5rem' }}>
        {loading ? '...' : visible && password ? password : '••••••••'}
      </span>
      <button onClick={handleToggle} data-testid={`toggle-pass-${userId}`}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--sapContent_IconColor)' }}>
        {visible ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </span>
  );
};

const COLORS = ['#0a6ed1', '#107e3e', '#e9730c', '#bb0000', '#6a6d70', '#354a5f', '#0854a0', '#1a9898'];

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const { t, getMonths } = useLanguage();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, total_records: 0, records_per_user: {} });
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newOperatorUrl, setNewOperatorUrl] = useState("");
  const [newUserType, setNewUserType] = useState("ACS");
  const [newUserLanguage, setNewUserLanguage] = useState("es");
  const [newDistId, setNewDistId] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Expanded user profile
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editOperatorUrl, setEditOperatorUrl] = useState("");
  const [editUserType, setEditUserType] = useState("");
  const [editUserLanguage, setEditUserLanguage] = useState("");
  const [editDistId, setEditDistId] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [passwordTargetUser, setPasswordTargetUser] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPasswordChange, setNewPasswordChange] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'records' or 'user'
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1); // 1 = first confirm, 2 = second confirm

  // Delete user modal
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [userToDeleteFull, setUserToDeleteFull] = useState(null);
  const [deleteUserConfirmStep, setDeleteUserConfirmStep] = useState(1);
  const [deletingUser, setDeletingUser] = useState(false);

  const [viewDataModalOpen, setViewDataModalOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [userData, setUserData] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [userTotal, setUserTotal] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Sorting for user data
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [filterMode, setFilterMode] = useState("month");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const months = getMonths();

  // Sorting function
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const sortedUserData = [...userData].sort((a, b) => {
    if (!sortColumn) return 0;
    const aVal = a[sortColumn] || '';
    const bVal = b[sortColumn] || '';
    
    if (sortDirection === 'asc') {
      return aVal.toString().localeCompare(bVal.toString());
    } else {
      return bVal.toString().localeCompare(aVal.toString());
    }
  });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally { setLoading(false); }
  }, [token, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => { logout(); navigate("/login"); toast.success(t('sessionClosed')); };

  const handleFileUpload = async (file) => {
    if (!file || !selectedUser) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload/${selectedUser.id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.records_count} ${t('recordsUploaded')}`);
      setUploadModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newName || !newPassword) { toast.error(t('fillAllFields')); return; }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/auth/register`, {
        username: newName, password: newPassword, name: newName, email: newEmail, 
        operator_url: newOperatorUrl, user_type: newUserType, user_language: newUserLanguage, 
        dist_id: newDistId, role: "user"
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('userCreated'));
      setCreateUserModalOpen(false);
      setNewUsername(""); setNewPassword(""); setNewName(""); setNewEmail(""); 
      setNewOperatorUrl(""); setNewUserType("ACS"); setNewUserLanguage("es"); setNewDistId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorCreating'));
    } finally { setCreatingUser(false); }
  };

  const toggleExpandUser = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setEditingProfile(null);
    } else {
      setExpandedUserId(userId);
      setEditingProfile(null);
    }
  };

  const startEditProfile = (u) => {
    setEditingProfile(u.id);
    setEditName(u.name || "");
    setEditEmail(u.email || "");
    setEditOperatorUrl(u.operator_url || "");
    setEditUserType(u.user_type || "ACS");
    setEditUserLanguage(u.user_language || "es");
    setEditDistId(u.dist_id || "");
    setEditPassword("");
  };

  const [editPassword, setEditPassword] = useState("");

  const handleSaveProfile = async (userId) => {
    setSavingProfile(true);
    try {
      // Update profile data
      await axios.put(`${API}/users/${userId}`, {
        name: editName,
        email: editEmail,
        operator_url: editOperatorUrl,
        user_type: editUserType,
        user_language: editUserLanguage,
        dist_id: editDistId
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // If password was changed, update it too
      if (editPassword && editPassword.length >= 4) {
        await axios.post(`${API}/users/${userId}/password`, {
          new_password: editPassword
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      toast.success(t('profileUpdated'));
      setEditingProfile(null);
      setEditPassword("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorUpdating'));
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPasswordChange || !confirmNewPassword) { toast.error(t('fillAllFields')); return; }
    if (newPasswordChange !== confirmNewPassword) { toast.error("Passwords don't match"); return; }
    setChangingPassword(true);
    try {
      const endpoint = passwordTargetUser?.id === user.id ? `${API}/auth/change-password` : `${API}/users/${passwordTargetUser.id}/password`;
      const body = passwordTargetUser?.id === user.id 
        ? { current_password: currentPassword, new_password: newPasswordChange }
        : { new_password: newPasswordChange };
      await axios.post(endpoint, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('passwordUpdated'));

  // Export function with authentication
  const handleExport = async (format, userId) => {
    try {
      const params = new URLSearchParams();
      params.append('user_id', userId);
      params.append('format', format);
      if (filterMode === 'year' && selectedYear) params.append('year', selectedYear);
      if (filterMode === 'month' && selectedYear && selectedMonth) {
        params.append('year', selectedYear);
        params.append('month', selectedMonth);
      }
      if (filterMode === 'custom' && startDate) params.append('start_date', startDate.toISOString());
      if (filterMode === 'custom' && endDate) params.append('end_date', endDate.toISOString());
      
      const response = await axios.get(`${API}/sales/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_${userId.substring(0, 8)}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('exportData') + ' ' + format.toUpperCase());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    }
  };
      setChangePasswordModalOpen(false);
      setCurrentPassword(""); setNewPasswordChange(""); setConfirmNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setChangingPassword(false); }
  };

  const handleDeleteRecords = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/sales/${userToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('recordsDeleted'));
      setDeleteModalOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setDeleting(false); }
  };

  // Delete user with double confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  
  const openDeleteUserModal = (userItem) => {
    setUserToDeleteFull(userItem);
    setDeleteUserConfirmStep(1);
    setDeleteConfirmText("");
    setDeleteUserModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDeleteFull) return;
    setDeletingUser(true);
    try {
      await axios.delete(`${API}/users/${userToDeleteFull.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('userDeleted'));
      setDeleteUserModalOpen(false);
      setUserToDeleteFull(null);
      setDeleteUserConfirmStep(1);
      setDeleteConfirmText("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setDeletingUser(false); }
  };

  const openViewDataModal = async (userItem) => {
    setViewingUser(userItem);
    setViewDataModalOpen(true);
    setShowHistory(false);
    setStartDate(null);
    setEndDate(null);
    setSortColumn(null);
    setSortDirection('asc');
    
    try {
      const [yearsRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/sales/years?user_id=${userItem.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales/stats?user_id=${userItem.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/imports/${userItem.id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Auto-select first available year and current/first month
      const years = yearsRes.data.years || [];
      setAvailableYears(years);
      setImportHistory(historyRes.data.history || []);
      
      const latestYear = yearsRes.data.latest_year;
      const latestMonth = yearsRes.data.latest_month;
      if (latestYear) {
        setSelectedYear(latestYear.toString());
        setSelectedMonth(latestMonth ? latestMonth.toString() : "1");
      } else if (years.length > 0) {
        setSelectedYear(years[0].toString());
        setSelectedMonth("1");
      }
      
      // Default to All Time to show all available data
      setFilterMode("all");
      fetchUserData(userItem.id, "", "", null, null, "all");
    } catch (error) {
      setAvailableYears([]);
      setImportHistory([]);
      setFilterMode("all");
    }
  };

  const fetchUserData = async (userId, year, month, start, end, mode = filterMode) => {
    setUserDataLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('user_id', userId);
      params.append('limit', '0'); // 0 means fetch all records without pagination
      const statsParams = new URLSearchParams();
      statsParams.append('user_id', userId);
      
      if (mode === "month" && year && month) {
        params.append('year', year); params.append('month', month);
        statsParams.append('year', year); statsParams.append('month', month);
      } else if (mode === "year" && year) {
        params.append('year', year);
        statsParams.append('year', year);
      } else if (mode === "custom") {
        if (start) { params.append('start_date', start.toISOString()); statsParams.append('start_date', start.toISOString()); }
        if (end) { params.append('end_date', end.toISOString()); statsParams.append('end_date', end.toISOString()); }
      }

      const [dataRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/sales/stats?${statsParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setUserData(dataRes.data.data || []);
      setUserTotal(dataRes.data.total || 0);
      setUserStats(statsRes.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally { setUserDataLoading(false); }
  };

  useEffect(() => {
    if (viewingUser) {
      fetchUserData(viewingUser.id, selectedYear, selectedMonth, startDate, endDate, filterMode);
    }
  }, [selectedYear, selectedMonth, startDate, endDate, filterMode]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try { return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return dateStr; }
  };

  const columns = [
    { key: 'ref', label: 'Ref' }, { key: 'fulfillment', label: 'Fulfillment' },
    { key: 'operatorurl', label: 'OperatorURL' }, { key: 'billingid', label: 'BillingID' },
    { key: 'transactiontime', label: 'Transaction Time', format: formatDate },
    { key: 'transactiontype', label: 'Transaction Type' }, { key: 'user', label: 'User' },
    { key: 'resource', label: 'Resource' }, { key: 'oldperm', label: 'Old Perm' },
    { key: 'newperm', label: 'New Perm' }, { key: 'resourcetype', label: 'Resource Type' },
    { key: 'devicetype', label: 'Device Type' }, { key: 'softwareversion', label: 'Software Version' },
    { key: 'distid', label: 'Dist ID' }, { key: 'clientsoftwareversion', label: 'Client SW Version' },
    { key: 'clientversion', label: 'Client Version' }, { key: 'clientos', label: 'Client OS' },
  ];

  if (loading) {
    return (
      <div className="fiori-login-page">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--sapBrandColor)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* SAP Shell Header */}
      <header className="sap-shell-header">
        <div className="sap-shell-logo">
          <div className="sap-shell-logo-icon"><BarChart3 size={18} /></div>
          <span className="sap-shell-title">{t('appName')}</span>
        </div>
        <div className="sap-shell-actions">
          <LanguageSelector />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="sap-shell-button" onClick={() => { setPasswordTargetUser(user); setChangePasswordModalOpen(true); }}>
                  <div className="sap-shell-avatar">{user?.username?.charAt(0).toUpperCase()}</div>
                  <span>{user?.username}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent><p>{t('profileTooltip')}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="sap-shell-button" onClick={handleLogout}>
                  <LogOut size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>{t('logoutTooltip')}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </header>

      <div className="fiori-layout">
        {/* Sidebar */}
        <aside className="fiori-sidebar" data-testid="admin-sidebar">
          <div className="fiori-sidebar-section">
            <div className="fiori-sidebar-title">{t('navigation')}</div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item active">
                    <Home size={18} className="fiori-nav-icon" />
                    <span>{t('adminPanel')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('adminPanelTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={() => setCreateUserModalOpen(true)}>
                    <UserPlus size={18} className="fiori-nav-icon" />
                    <span>{t('createUser')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('createUserTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="fiori-sidebar-section">
            <div className="fiori-sidebar-title">{t('settings')}</div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={() => { setPasswordTargetUser(user); setChangePasswordModalOpen(true); }}>
                    <Key size={18} className="fiori-nav-icon" />
                    <span>{t('changePassword')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('changePasswordTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="fiori-nav-item" onClick={handleLogout} style={{ color: 'var(--sapErrorColor)' }}>
                    <LogOut size={18} className="fiori-nav-icon" style={{ color: 'var(--sapErrorColor)' }} />
                    <span>{t('logout')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right"><p>{t('logoutTooltip')}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </aside>

        {/* Main Content */}
        <main className="fiori-main">
          <div className="fiori-page-header">
            <h1 className="fiori-page-title">{t('adminPanel')}</h1>
            <p className="fiori-page-subtitle">{t('manageUsersAndData')}</p>
          </div>

          {/* Stats Tiles */}
          <div className="fiori-tiles-grid">
            <div className="fiori-tile">
              <div className="fiori-tile-header"><div className="fiori-tile-icon brand"><Users size={20} /></div></div>
              <div className="fiori-tile-value">{stats.total_users}</div>
              <div className="fiori-tile-label">{t('totalUsers')}</div>
            </div>
            <div className="fiori-tile">
              <div className="fiori-tile-header"><div className="fiori-tile-icon success"><Database size={20} /></div></div>
              <div className="fiori-tile-value">{stats.total_records}</div>
              <div className="fiori-tile-label">{t('totalRecords')}</div>
            </div>
          </div>

          {/* Users Table */}
          <div className="fiori-table-container">
            <div className="fiori-table-header">
              <div>
                <span className="fiori-table-title">{t('users')}</span>
                <span className="fiori-table-count">({users.length})</span>
              </div>
              <button className="fiori-button emphasized" onClick={() => setCreateUserModalOpen(true)}>
                <UserPlus size={14} /> {t('createUser')}
              </button>
            </div>
            <div className="fiori-table-scroll" style={{ maxHeight: '400px' }}>
              <table className="fiori-table" style={{ minWidth: '900px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>{t('username')}</th>
                    <th>{t('passwordColumn')}</th>
                    <th>{t('contactEmail')}</th>
                    <th>{t('userType')}</th>
                    <th>{t('role')}</th>
                    <th>{t('records')}</th>
                    <th style={{ textAlign: 'right' }}>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <Fragment key={u.id}>
                      <tr style={{ backgroundColor: expandedUserId === u.id ? 'var(--sapList_SelectionBackgroundColor)' : 'transparent' }}>
                        <td style={{ padding: '0.5rem' }}>
                          <button 
                            className="fiori-button ghost" 
                            onClick={() => toggleExpandUser(u.id)}
                            style={{ padding: '0.25rem' }}
                          >
                            {expandedUserId === u.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                        <td>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button 
                                  onClick={() => openViewDataModal(u)}
                                  style={{ background: 'none', border: 'none', color: 'var(--sapBrandColor)', cursor: 'pointer', fontWeight: 500 }}
                                >
                                  {u.username}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent><p>{t('clickToViewData')}</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td>
                          <PasswordCell userId={u.id} token={token} />
                        </td>
                        <td>{u.email || '-'}</td>
                        <td>{u.user_type || '-'}</td>
                        <td>
                          <span className={`fiori-status ${u.role === 'admin' ? 'info' : 'success'}`}>
                            {u.role === 'admin' ? t('administrator') : t('user')}
                          </span>
                        </td>
                        <td>{stats.records_per_user?.[u.id] || 0}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="fiori-button ghost" onClick={() => openViewDataModal(u)}>
                                    <Eye size={14} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent><p>{t('viewDataTooltip')}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="fiori-button ghost" onClick={() => { setSelectedUser(u); setUploadModalOpen(true); }}>
                                    <Upload size={14} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent><p>{t('uploadExcelTooltip')}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {u.role !== 'admin' && (
                              <>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="fiori-button ghost" onClick={() => { setUserToDelete(u); setDeleteModalOpen(true); }}
                                        style={{ color: 'var(--sapErrorColor)' }}>
                                        <Trash2 size={14} />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{t('deleteRecordsTooltip')}</p></TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="fiori-button ghost" onClick={() => openDeleteUserModal(u)}
                                        style={{ color: 'var(--sapErrorColor)' }}>
                                        <UserX size={14} />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{t('deleteUserTooltip')}</p></TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded Profile Card */}
                      {expandedUserId === u.id && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, backgroundColor: 'var(--sapBackgroundColor)' }}>
                            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--sapList_BorderColor)', borderBottom: '1px solid var(--sapList_BorderColor)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--sapTextColor)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <User size={16} />
                                  {t('userProfile')}: {u.username}
                                </h4>
                                {editingProfile !== u.id ? (
                                  <button className="fiori-button ghost" onClick={() => startEditProfile(u)}>
                                    <Edit size={14} /> {t('editProfile')}
                                  </button>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button className="fiori-button ghost" onClick={() => setEditingProfile(null)}>
                                      {t('cancel')}
                                    </button>
                                    <button className="fiori-button emphasized" onClick={() => handleSaveProfile(u.id)} disabled={savingProfile}>
                                      <Save size={14} /> {savingProfile ? t('saving') : t('save')}
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <User size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('clientName')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <input
                                      className="fiori-form-input"
                                      value={editName}
                                      onChange={(e) => setEditName(e.target.value)}
                                    />
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>{u.name || '-'}</p>
                                  )}
                                </div>
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <Mail size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('contactEmail')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <input
                                      type="email"
                                      className="fiori-form-input"
                                      value={editEmail}
                                      onChange={(e) => setEditEmail(e.target.value)}
                                    />
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>{u.email || '-'}</p>
                                  )}
                                </div>
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <Globe size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('operatorUrl')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <input
                                      type="url"
                                      className="fiori-form-input"
                                      value={editOperatorUrl}
                                      onChange={(e) => setEditOperatorUrl(e.target.value)}
                                      placeholder="https://..."
                                    />
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>
                                      {u.operator_url ? (
                                        <a href={u.operator_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--sapBrandColor)' }}>
                                          {u.operator_url}
                                        </a>
                                      ) : '-'}
                                    </p>
                                  )}
                                </div>
                                {/* Type field */}
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <Tag size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('userType')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <select
                                      className="fiori-select"
                                      value={editUserType}
                                      onChange={(e) => setEditUserType(e.target.value)}
                                      style={{ width: '100%' }}
                                    >
                                      <option value="ACS">{t('typeACS')}</option>
                                      <option value="ApplicaDigital">{t('typeApplicaDigital')}</option>
                                    </select>
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>
                                      {u.user_type || '-'}
                                    </p>
                                  )}
                                </div>
                                {/* Language field */}
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <Languages size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('userLanguage')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <select
                                      className="fiori-select"
                                      value={editUserLanguage}
                                      onChange={(e) => setEditUserLanguage(e.target.value)}
                                      style={{ width: '100%' }}
                                    >
                                      <option value="es">{t('langSpanish')}</option>
                                      <option value="en">{t('langEnglish')}</option>
                                    </select>
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>
                                      {u.user_language === 'en' ? t('langEnglish') : u.user_language === 'es' ? t('langSpanish') : '-'}
                                    </p>
                                  )}
                                </div>
                                {/* DistID field */}
                                <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                  <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                    <Hash size={12} style={{ marginRight: '0.25rem' }} />
                                    {t('distId')}
                                  </label>
                                  {editingProfile === u.id ? (
                                    <input
                                      type="text"
                                      className="fiori-form-input"
                                      value={editDistId}
                                      onChange={(e) => setEditDistId(e.target.value)}
                                    />
                                  ) : (
                                    <p style={{ fontSize: '0.875rem', color: 'var(--sapTextColor)', padding: '0.5rem 0' }}>
                                      {u.dist_id || '-'}
                                    </p>
                                  )}
                                </div>
                                {/* Password field - only visible when editing */}
                                {editingProfile === u.id && (
                                  <div className="fiori-form-group" style={{ marginBottom: 0 }}>
                                    <label className="fiori-form-label" style={{ fontSize: '0.75rem' }}>
                                      <Key size={12} style={{ marginRight: '0.25rem' }} />
                                      {t('newPassword')}
                                    </label>
                                    <input
                                      type="text"
                                      className="fiori-form-input"
                                      value={editPassword}
                                      onChange={(e) => setEditPassword(e.target.value)}
                                      style={{ fontFamily: 'monospace' }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('uploadExcel')}</DialogTitle>
            <DialogDescription style={{ color: 'var(--sapContent_LabelColor)' }}>
              {t('uploadFor')}: {selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="fiori-dialog-content">
            <div
              style={{
                border: `2px dashed ${dragOver ? 'var(--sapBrandColor)' : 'var(--sapList_BorderColor)'}`,
                borderRadius: '4px', padding: '2rem', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? 'rgba(10,110,209,0.05)' : 'transparent', transition: 'all 0.15s'
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload size={32} style={{ color: 'var(--sapBrandColor)', marginBottom: '0.5rem' }} />
              <p style={{ color: 'var(--sapTextColor)', marginBottom: '0.25rem' }}>{t('dragDropExcel')}</p>
              <p style={{ color: 'var(--sapContent_LabelColor)', fontSize: '0.8125rem' }}>{t('orClickToSelect')}</p>
              <input id="file-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e.target.files?.[0])} />
            </div>
            {uploading && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[var(--sapBrandColor)] border-t-transparent mx-auto"></div>
                <p style={{ marginTop: '0.5rem', color: 'var(--sapContent_LabelColor)' }}>{t('uploading')}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={createUserModalOpen} onOpenChange={setCreateUserModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('createUser')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="fiori-dialog-content">
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('fullName')} *</label>
              <input className="fiori-form-input" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="create-user-name" />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('password')} *</label>
              <input type="password" className="fiori-form-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('contactEmail')}</label>
              <input type="email" className="fiori-form-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('operatorUrl')}</label>
              <input type="url" className="fiori-form-input" value={newOperatorUrl} onChange={(e) => setNewOperatorUrl(e.target.value)} placeholder="https://..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="fiori-form-group">
                <label className="fiori-form-label">{t('userType')}</label>
                <select className="fiori-select" value={newUserType} onChange={(e) => setNewUserType(e.target.value)} style={{ width: '100%' }}>
                  <option value="ACS">{t('typeACS')}</option>
                  <option value="ApplicaDigital">{t('typeApplicaDigital')}</option>
                </select>
              </div>
              <div className="fiori-form-group">
                <label className="fiori-form-label">{t('userLanguage')}</label>
                <select className="fiori-select" value={newUserLanguage} onChange={(e) => setNewUserLanguage(e.target.value)} style={{ width: '100%' }}>
                  <option value="es">{t('langSpanish')}</option>
                  <option value="en">{t('langEnglish')}</option>
                </select>
              </div>
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('distId')}</label>
              <input className="fiori-form-input" value={newDistId} onChange={(e) => setNewDistId(e.target.value)} data-testid="create-user-distid" />
            </div>
            <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
              <button type="button" className="fiori-button" onClick={() => setCreateUserModalOpen(false)}>{t('cancel')}</button>
              <button type="submit" className="fiori-button emphasized" disabled={creatingUser}>
                {creatingUser ? t('creating') : t('create')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={changePasswordModalOpen} onOpenChange={setChangePasswordModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('changePasswordTitle')}</DialogTitle>
            {passwordTargetUser && <DialogDescription style={{ color: 'var(--sapContent_LabelColor)' }}>
              {t('changePasswordFor')}: {passwordTargetUser.username}
            </DialogDescription>}
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="fiori-dialog-content">
            {passwordTargetUser?.id === user.id && (
              <div className="fiori-form-group">
                <label className="fiori-form-label">{t('currentPassword')}</label>
                <input type="password" className="fiori-form-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
              </div>
            )}
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('newPassword')}</label>
              <input type="password" className="fiori-form-input" value={newPasswordChange} onChange={(e) => setNewPasswordChange(e.target.value)} />
            </div>
            <div className="fiori-form-group">
              <label className="fiori-form-label">{t('confirmPassword')}</label>
              <input type="password" className="fiori-form-input" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
            </div>
            <div className="fiori-dialog-footer" style={{ padding: 0, border: 'none', marginTop: '1rem' }}>
              <button type="button" className="fiori-button" onClick={() => setChangePasswordModalOpen(false)}>{t('cancel')}</button>
              <button type="submit" className="fiori-button emphasized" disabled={changingPassword}>
                {changingPassword ? t('changing') : t('changePassword')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Records Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header">
            <DialogTitle className="fiori-dialog-title">{t('confirmDelete')}</DialogTitle>
          </DialogHeader>
          <div className="fiori-dialog-content">
            <p style={{ color: 'var(--sapTextColor)' }}>{t('deleteRecordsConfirm')} <strong>{userToDelete?.username}</strong>?</p>
            <p style={{ color: 'var(--sapErrorColor)', fontSize: '0.875rem', marginTop: '0.5rem' }}>{t('actionIrreversible')}</p>
          </div>
          <div className="fiori-dialog-footer">
            <button className="fiori-button" onClick={() => setDeleteModalOpen(false)}>{t('cancel')}</button>
            <button className="fiori-button emphasized" style={{ backgroundColor: 'var(--sapErrorColor)', borderColor: 'var(--sapErrorColor)' }}
              onClick={handleDeleteRecords} disabled={deleting}>
              {deleting ? t('deleting') : t('delete')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Modal - Double Confirmation */}
      <Dialog open={deleteUserModalOpen} onOpenChange={(open) => { 
        if (!open) { setDeleteUserConfirmStep(1); setDeleteConfirmText(""); }
        setDeleteUserModalOpen(open);
      }}>
        <DialogContent className="fiori-dialog sm:max-w-md">
          <DialogHeader className="fiori-dialog-header" style={{ backgroundColor: 'var(--sapErrorBackground)', borderColor: 'var(--sapErrorBorderColor)' }}>
            <DialogTitle className="fiori-dialog-title" style={{ color: 'var(--sapErrorColor)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={20} />
              {t('deleteUserTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="fiori-dialog-content">
            {deleteUserConfirmStep === 1 ? (
              <>
                <p style={{ color: 'var(--sapTextColor)', marginBottom: '1rem' }}>
                  {t('deleteUserConfirm1')} <strong style={{ color: 'var(--sapErrorColor)' }}>{userToDeleteFull?.username}</strong>?
                </p>
                <div style={{ backgroundColor: 'var(--sapErrorBackground)', padding: '1rem', borderRadius: '4px', border: '1px solid var(--sapErrorBorderColor)' }}>
                  <p style={{ color: 'var(--sapErrorColor)', fontSize: '0.875rem', fontWeight: 500 }}>
                    {t('deleteUserConfirm2')}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--sapTextColor)', marginBottom: '1rem' }}>
                  {t('deleteUserFinalConfirm')} <strong style={{ color: 'var(--sapErrorColor)' }}>{userToDeleteFull?.username}</strong>
                </p>
                <div className="fiori-form-group">
                  <input
                    type="text"
                    className="fiori-form-input"
                    style={{ borderColor: 'var(--sapErrorBorderColor)' }}
                    placeholder={t('typeDelete')}
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    autoFocus
                  />
                </div>
              </>
            )}
          </div>
          <div className="fiori-dialog-footer">
            <button className="fiori-button" onClick={() => { setDeleteUserModalOpen(false); setDeleteUserConfirmStep(1); setDeleteConfirmText(""); }}>
              {t('cancel')}
            </button>
            {deleteUserConfirmStep === 1 ? (
              <button 
                className="fiori-button emphasized" 
                style={{ backgroundColor: 'var(--sapErrorColor)', borderColor: 'var(--sapErrorColor)' }}
                onClick={() => setDeleteUserConfirmStep(2)}
              >
                {t('confirm')}
              </button>
            ) : (
              <button 
                className="fiori-button emphasized" 
                style={{ backgroundColor: 'var(--sapErrorColor)', borderColor: 'var(--sapErrorColor)' }}
                onClick={handleDeleteUser}
                disabled={deletingUser || (deleteConfirmText !== 'DELETE' && deleteConfirmText !== 'ELIMINAR')}
              >
                {deletingUser ? t('deleting') : t('deleteUser')}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View User Data Modal */}
      <Dialog open={viewDataModalOpen} onOpenChange={setViewDataModalOpen}>
        <DialogContent className="fiori-dialog" style={{ maxWidth: '90vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--sapList_BorderColor)', background: 'var(--sapShellColor)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <DialogTitle style={{ color: 'white', fontSize: '1.125rem' }}>{t('dashboard')}: {viewingUser?.username}</DialogTitle>
              <DialogDescription style={{ color: 'rgba(255,255,255,0.7)' }}>
                {viewingUser?.name && <span>{viewingUser.name} • </span>}
                {viewingUser?.email && <span>{viewingUser.email}</span>}
              </DialogDescription>
            </div>
            <button className="fiori-button" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
              onClick={() => setShowHistory(!showHistory)}>
              <Clock size={14} /> {showHistory ? t('dashboard') : t('importHistory')}
            </button>
          </div>
          
          {showHistory ? (
            <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem', background: 'var(--sapBackgroundColor)' }}>
              <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem' }}>{t('importHistory')}</h3>
              {importHistory.length === 0 ? (
                <div className="fiori-empty-state">
                  <FileSpreadsheet size={48} className="fiori-empty-state-icon" />
                  <p>{t('noImports')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {importHistory.map((item, idx) => (
                    <div key={item.id || idx} className="fiori-card">
                      <div className="fiori-card-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <p style={{ fontWeight: 500 }}>{item.filename}</p>
                          <p style={{ fontSize: '0.8125rem', color: 'var(--sapContent_LabelColor)', marginTop: '0.25rem' }}>
                            {new Date(item.imported_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--sapBrandColor)' }}>+{item.records_count}</p>
                          <p style={{ fontSize: '0.75rem', color: 'var(--sapContent_LabelColor)' }}>{t('records')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="fiori-toolbar" style={{ margin: 0, borderRadius: 0, borderLeft: 'none', borderRight: 'none' }}>
                <Filter size={16} style={{ color: 'var(--sapContent_LabelColor)' }} />
                <select className="fiori-select" value={filterMode} onChange={(e) => { setFilterMode(e.target.value); }}>
                  <option value="month">{t('month')}</option>
                  <option value="year">{t('year')}</option>
                  <option value="all">{t('allTime')}</option>
                  <option value="custom">{t('customRange')}</option>
                </select>
                {(filterMode === "month" || filterMode === "year") && (
                  <select className="fiori-select" value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value); }}>
                    {availableYears.map((y) => (<option key={y} value={y}>{y}</option>))}
                  </select>
                )}
                {filterMode === "month" && (
                  <select className="fiori-select" value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); }}>
                    {months.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                  </select>
                )}
                {filterMode === "custom" && (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="fiori-button ghost"><CalendarIcon size={14} /> {startDate ? format(startDate, "dd/MM/yyyy") : t('from')}</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={(d) => { setStartDate(d); }} /></PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="fiori-button ghost"><CalendarIcon size={14} /> {endDate ? format(endDate, "dd/MM/yyyy") : t('to')}</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={(d) => { setEndDate(d); }} /></PopoverContent>
                    </Popover>
                  </>
                )}
                <div className="fiori-toolbar-spacer" />
                <div className="fiori-info-label">{t('totalRecords')}: <span className="fiori-info-value">{userStats?.total_all_records || 0}</span></div>
                <div className="fiori-toolbar-separator" />
                <div className="fiori-info-label">{t('selectedRecords')}: <span className="fiori-info-value" style={{ color: 'var(--sapBrandColor)' }}>{userStats?.total_selected || 0}</span></div>
                <div className="fiori-toolbar-separator" />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    className="fiori-button ghost"
                    onClick={() => handleExport('csv', viewingUser.id)}
                  >
                    <Download size={14} /> CSV
                  </button>
                  <button 
                    className="fiori-button ghost"
                    onClick={() => handleExport('xlsx', viewingUser.id)}
                  >
                    <Download size={14} /> Excel
                  </button>
                </div>
              </div>

              {/* Content */}
              <div style={{ flex: 1, overflow: 'auto', padding: '1rem', background: 'var(--sapBackgroundColor)' }}>
                {userDataLoading ? (
                  <div className="fiori-empty-state">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--sapBrandColor)] border-t-transparent mx-auto"></div>
                  </div>
                ) : (
                  <>
                    {/* Stats Tiles */}
                    <div className="fiori-tiles-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                      <div className="fiori-tile">
                        <div className="fiori-tile-header"><div className="fiori-tile-icon brand"><Database size={18} /></div></div>
                        <div className="fiori-tile-value" style={{ fontSize: '1.5rem' }}>{userStats?.total_all_records || 0}</div>
                        <div className="fiori-tile-label">{t('totalRecords')}</div>
                      </div>
                      <div className="fiori-tile">
                        <div className="fiori-tile-header"><div className="fiori-tile-icon success"><Filter size={18} /></div></div>
                        <div className="fiori-tile-value" style={{ fontSize: '1.5rem' }}>{userStats?.total_selected || 0}</div>
                        <div className="fiori-tile-label">{t('selectedRecords')}</div>
                      </div>
                      <div className="fiori-tile">
                        <div className="fiori-tile-header"><div className="fiori-tile-icon warning"><BarChart3 size={18} /></div></div>
                        <div className="fiori-tile-value" style={{ fontSize: '1.5rem' }}>{userStats?.client_versions?.length || 0}</div>
                        <div className="fiori-tile-label">{t('clientVersions')}</div>
                      </div>
                      <div className="fiori-tile">
                        <div className="fiori-tile-header"><div className="fiori-tile-icon neutral"><Clock size={18} /></div></div>
                        <div className="fiori-tile-value" style={{ fontSize: '1.5rem' }}>{userStats?.last_import_count || 0}</div>
                        <div className="fiori-tile-label">{t('lastImport')}</div>
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="fiori-charts-grid">
                      <div className="fiori-card">
                        <div className="fiori-card-header"><h3 className="fiori-card-title">{t('transactionsByMonth')}</h3></div>
                        <div className="fiori-card-content">
                          {userStats?.monthly_data?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={180}>
                              <LineChart data={userStats.monthly_data}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e4" />
                                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6a6d70' }} stroke="#e4e4e4" />
                                <YAxis tick={{ fontSize: 10, fill: '#6a6d70' }} stroke="#e4e4e4" />
                                <RechartsTooltip />
                                <Line type="monotone" dataKey="count" stroke="#0a6ed1" strokeWidth={2} dot={{ fill: '#0a6ed1', r: 2 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : <div className="fiori-empty-state"><p>{t('noDataAvailable')}</p></div>}
                        </div>
                      </div>
                      <div className="fiori-card">
                        <div className="fiori-card-header"><h3 className="fiori-card-title">{t('clientOSDistribution')}</h3></div>
                        <div className="fiori-card-content">
                          {userStats?.client_os?.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <ResponsiveContainer width="50%" height={160}>
                                <PieChart>
                                  <Pie data={userStats.client_os.slice(0, 6)} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={60}>
                                    {userStats.client_os.slice(0, 6).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                                  </Pie>
                                  <RechartsTooltip />
                                </PieChart>
                              </ResponsiveContainer>
                              <div style={{ flex: 1, fontSize: '0.75rem' }}>
                                {userStats.client_os.slice(0, 5).map((item, i) => (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: COLORS[i % COLORS.length] }} />
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                    <span style={{ fontWeight: 600 }}>{item.percentage}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : <div className="fiori-empty-state"><p>{t('noDataAvailable')}</p></div>}
                        </div>
                      </div>
                    </div>

                    {/* Data Table */}
                    <div className="fiori-table-container">
                      <div className="fiori-table-header">
                        <div>
                          <span className="fiori-table-title">{t('dataTable')}</span>
                          <span className="fiori-table-count">({userTotal} {t('records')})</span>
                        </div>
                        {sortColumn && (
                          <button className="fiori-button ghost" onClick={() => { setSortColumn(null); setSortDirection('asc'); }}>
                            {t('clearSort')}
                          </button>
                        )}
                      </div>
                      {userData.length === 0 ? (
                        <div className="fiori-empty-state">
                          <FileSpreadsheet size={40} className="fiori-empty-state-icon" />
                          <p className="fiori-empty-state-title">{t('noData')}</p>
                        </div>
                      ) : (
                        <>
                          <div className="fiori-table-scroll" style={{ maxHeight: '400px' }}>
                            <table className="fiori-table">
                              <thead>
                                <tr>
                                  <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                  {columns.map((col) => (
                                    <th 
                                      key={col.key} 
                                      onClick={() => handleSort(col.key)}
                                      style={{ cursor: 'pointer', userSelect: 'none' }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {col.label}
                                        {sortColumn === col.key ? (
                                          sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                                        ) : (
                                          <ArrowUpDown size={14} style={{ opacity: 0.3 }} />
                                        )}
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sortedUserData.map((record, idx) => (
                                  <tr key={record.id || idx}>
                                    <td style={{ textAlign: 'center', color: 'var(--sapContent_LabelColor)', fontWeight: 500 }}>{idx + 1}</td>
                                    {columns.map((col) => (
                                      <td key={col.key}>{col.format ? col.format(record[col.key]) : (record[col.key] || '-')}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="fiori-table-footer">
                            <span style={{ fontSize: '0.8125rem', color: 'var(--sapContent_LabelColor)' }}>
                              {t('showingAllRecords')}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
